import { useEffect, useRef, useCallback } from "react"

interface SwipeConfig {
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  threshold?: number // minimum distance for swipe
  edgeThreshold?: number // distance from edge to trigger
  enabled?: boolean
}

export const useSwipeGesture = ({
  onSwipeLeft,
  onSwipeRight,
  threshold = 50,
  edgeThreshold = 30,
  enabled = true
}: SwipeConfig) => {
  const touchStartX = useRef<number | null>(null)
  const touchStartY = useRef<number | null>(null)
  const touchStartedFromEdge = useRef<"left" | "right" | null>(null)

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled) return
    
    const touch = e.touches[0]
    touchStartX.current = touch.clientX
    touchStartY.current = touch.clientY
    
    // Check if touch started from edge
    const screenWidth = window.innerWidth
    if (touch.clientX <= edgeThreshold) {
      touchStartedFromEdge.current = "left"
    } else if (touch.clientX >= screenWidth - edgeThreshold) {
      touchStartedFromEdge.current = "right"
    } else {
      touchStartedFromEdge.current = null
    }
  }, [enabled, edgeThreshold])

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (!enabled || touchStartX.current === null || touchStartY.current === null) return
    
    const touch = e.changedTouches[0]
    const deltaX = touch.clientX - touchStartX.current
    const deltaY = touch.clientY - touchStartY.current
    
    // Only trigger if horizontal movement is greater than vertical (not scrolling)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > threshold) {
      if (deltaX > 0 && touchStartedFromEdge.current === "left") {
        // Swipe right from left edge - could open menu
        onSwipeRight?.()
      } else if (deltaX < 0 && touchStartedFromEdge.current === "right") {
        // Swipe left from right edge - open menu
        onSwipeLeft?.()
      } else if (deltaX > 0 && !touchStartedFromEdge.current) {
        // General swipe right - close menu
        onSwipeRight?.()
      }
    }
    
    // Reset
    touchStartX.current = null
    touchStartY.current = null
    touchStartedFromEdge.current = null
  }, [enabled, threshold, onSwipeLeft, onSwipeRight])

  useEffect(() => {
    if (!enabled) return
    
    document.addEventListener("touchstart", handleTouchStart, { passive: true })
    document.addEventListener("touchend", handleTouchEnd, { passive: true })
    
    return () => {
      document.removeEventListener("touchstart", handleTouchStart)
      document.removeEventListener("touchend", handleTouchEnd)
    }
  }, [enabled, handleTouchStart, handleTouchEnd])
}

// Hook specifically for sheet/menu swipe detection on the element itself
export const useElementSwipe = (
  onSwipeRight?: () => void,
  threshold = 80
) => {
  const touchStartX = useRef<number | null>(null)
  
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
  }, [])
  
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartX.current === null) return
    
    const deltaX = e.changedTouches[0].clientX - touchStartX.current
    
    if (deltaX > threshold) {
      onSwipeRight?.()
    }
    
    touchStartX.current = null
  }, [onSwipeRight, threshold])
  
  return {
    onTouchStart: handleTouchStart,
    onTouchEnd: handleTouchEnd
  }
}
